import { useContext, useEffect, useRef } from "react";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import mapboxgl from "mapbox-gl";

import { MapContext } from "fond/map/MapProvider";
import { useStyleEpoch } from "fond/map/useStyleEpoch";

import { getGcpColor } from "./types";
import { useImageOverlay } from "./useImageOverlay";

import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

const SOURCE_ID = "image-overlay-gcp-source";
const CIRCLE_LAYER_ID = "image-overlay-gcp-circles";

/**
 * Manages map click handling for placing GCP map points,
 * and renders GCP markers on the map as a GeoJSON source + circle/symbol layers.
 */
const MapGCPHandler: React.FC = () => {
  const { map } = useContext(MapContext);
  const { gcps, activeGcpId, mode, setMapPoint } = useImageOverlay();
  const prevCursorRef = useRef<string>("");
  const styleEpoch = useStyleEpoch(map);

  // Add geocoder (search) control
  useEffect(() => {
    if (!map) return;

    const GeocoderControl = MapboxGeocoder as any;
    const geocoder = new GeocoderControl({
      accessToken: `${import.meta.env.REACT_APP_MAPBOX_KEY}`,
      mapboxgl,
      marker: true,
      collapsed: true,
      placeholder: "Search location...",
    });
    map.addControl(geocoder, "top-right");

    // eslint-disable-next-line consistent-return
    return () => {
      try {
        map.removeControl(geocoder);
      } catch {
        // Map container may already be destroyed when the modal closes
      }
    };
  }, [map]);

  // Handle map click for placing GCP map coordinates
  useEffect(() => {
    if (!map || mode !== "place-map" || !activeGcpId) return;

    prevCursorRef.current = map.getCanvas().style.cursor;
    map.getCanvas().style.cursor = "crosshair";

    const handler = (e: mapboxgl.MapMouseEvent) => {
      setMapPoint(activeGcpId, e.lngLat.lng, e.lngLat.lat);
    };

    map.on("click", handler);

    return () => {
      map.off("click", handler);
      map.getCanvas().style.cursor = prevCursorRef.current;
    };
  }, [map, mode, activeGcpId, setMapPoint]);

  // Manage GeoJSON source + layers for GCP markers on the map
  useEffect(() => {
    if (!map) return;

    const geojsonData: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: gcps
        .filter((gcp) => gcp.mapLng != null && gcp.mapLat != null)
        .map((gcp) => ({
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [gcp.mapLng!, gcp.mapLat!],
          },
          properties: {
            color: getGcpColor(gcp.colorIndex),
            isActive: gcp.id === activeGcpId,
          },
        })),
    };

    const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData(geojsonData);
    } else {
      map.addSource(SOURCE_ID, { type: "geojson", data: geojsonData });

      map.addLayer({
        id: CIRCLE_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-radius": 8,
          "circle-color": ["get", "color"],
          "circle-stroke-color": ["case", ["get", "isActive"], "#FFC107", "white"],
          "circle-stroke-width": ["case", ["get", "isActive"], 3, 2],
        },
      });
    }
  }, [map, gcps, activeGcpId, styleEpoch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      try {
        if (map.getLayer(CIRCLE_LAYER_ID)) map.removeLayer(CIRCLE_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        // Map may already be destroyed when the modal closes
      }
    };
  }, [map]);

  return null;
};

export default MapGCPHandler;
